import fetch from 'cross-fetch'
import {print} from 'graphql'

import {REFRESH_TOKEN, SIGN_IN, SIGN_OUT} from '../graphql/mutations/oauth'
import {getItem, removeItem, setAuthToken, setItem} from './authTools'

/**
 * Turning a sign-in into a session, keeping it one, and ending it.
 *
 * Nothing here goes through the Apollo client. The error link is what calls this, so routing the
 * mutation back through the same chain would put that link in the way of its own recovery, and
 * importing the client would close a circle between the two modules.
 */

// Named for the origin rather than the tab: two tabs of the drive share one storage, and the
// session in it must not be traded or renewed by both at once.
const SESSION_LOCK = 'drive-session'

// The trade is answered before it has finished: Capsa reports "exchanging" while the user the grant
// names is still being registered — it waits on a webhook the OAuth service queues — and hands back
// a blank pair until it lands. Nobody signing in can do anything about that but wait, so the trade
// waits on their behalf rather than reporting a failure.
const EXCHANGING = 'exchanging'

// Eight asks over about half a minute, the first of them straight away. The exchange usually lands
// between the early ones, and the widening is what keeps a slow one from turning into a poll
// against the very service that is busy finishing it.
const EXCHANGE_LADDER = [0, 600, 1200, 2400, 4000, 6000, 8000, 8000]

let inFlight = null

let exchanging = null

const post = async body => {
  const response = await fetch(`${API_HOST}/graphql`, {
    method: 'POST',
    headers: {'content-type': 'application/json'},
    body: JSON.stringify(body)
  })

  return response.json()
}

// The fallback is for the test environment, where there is no navigator.locks: the work still
// runs, just without the cross-tab guarantee.
const underLock = work => (
  typeof navigator === 'undefined' || !navigator.locks ?
    work() :
    navigator.locks.request(SESSION_LOCK, work)
)

const after = wait => new Promise(resolve => setTimeout(resolve, wait))

const askToTrade = async bearer => {
  try {
    return await post({
      query: print(SIGN_IN),
      variables: {input: {token: bearer}}
    })
  } catch {
    // No answer is not a refusal, and the bearer may still be good.
    return null
  }
}

const trade = async bearer => {
  // Read again now the lock is held. Another tab may have been trading this very bearer while we
  // queued behind it, and the bearer is single use.
  if (getItem('refreshToken')) return true

  // The lock stays held across the waiting, so a tab that queued behind this one finds the finished
  // session in storage and never spends the bearer a second time.
  for (const wait of EXCHANGE_LADDER) {
    if (wait) await after(wait)

    const payload = await askToTrade(bearer)

    if (!payload) return false

    if (payload.errors || !payload.data?.signIn) {
      removeItem('authToken')

      return false
    }

    const credentials = payload.data.signIn

    if (credentials.status !== EXCHANGING) {
      setAuthToken(credentials.authToken)
      setItem('refreshToken', credentials.refreshToken)

      return true
    }
  }

  // Still exchanging when the ladder ran out. The bearer is untouched and still good, so the next
  // trade picks the wait up rather than starting a sign-in over.
  return false
}

const renew = async sentWith => {
  // Read again now the lock is held. Whoever we queued behind may have renewed the very session
  // this request lapsed on, and asking a second time would spend the token they just wrote.
  if (getItem('authToken') !== sentWith) return true

  const refreshToken = getItem('refreshToken')

  // A bearer that was never traded has not lapsed — it was never a session. trade rather than
  // exchangeBearer, because the lock is already held.
  if (!refreshToken) return trade(sentWith)

  let payload

  try {
    payload = await post({
      query: print(REFRESH_TOKEN),
      variables: {input: {token: refreshToken}}
    })
  } catch {
    // The server never answered. The tokens may well still be good, so a dropped connection
    // doesn't get to end the session.
    return false
  }

  // Renewing rotates the pair, so one the server has turned down is spent, and keeping it only
  // earns the same answer next time.
  if (payload.errors || !payload.data?.refreshToken) {
    removeItem('authToken')
    removeItem('refreshToken')

    return false
  }

  setAuthToken(payload.data.refreshToken.authToken)
  setItem('refreshToken', payload.data.refreshToken.refreshToken)

  return true
}

/**
 * Trades the single-use bearer the code grant hands back for the auth and refresh pair everything
 * else assumes, if a bearer is what this browser is holding.
 *
 * Resolves true when there is now a session that there wasn't a moment ago. That answer can be up
 * to half a minute in coming, because it waits out Capsa finishing the registration.
 */
export const exchangeBearer = () => {
  const bearer = getItem('authToken')

  if (!bearer || getItem('refreshToken')) return Promise.resolve(false)

  // React runs an effect twice in development, and a bearer traded twice is refused the second time.
  if (!exchanging) {
    exchanging = underLock(() => trade(bearer)).finally(() => {
      exchanging = null
    })
  }

  return exchanging
}

/**
 * Renews the session, once, for every request that lapsed on the same one.
 *
 * Takes the token the caller's request went out carrying: a request that lapsed on a session
 * since renewed needs no renewal, only a retry on what is now stored.
 *
 * Resolves true when the session is good, false when it isn't, so the caller can choose between
 * retrying its operation and letting the original failure through.
 */
export const refreshSession = sentWith => {
  // A request that went out signed out had no session to lapse, so its refusal is its own.
  if (!sentWith) return Promise.resolve(false)

  if (getItem('authToken') !== sentWith) return Promise.resolve(true)

  if (!inFlight) {
    inFlight = underLock(() => renew(sentWith)).finally(() => {
      inFlight = null
    })
  }

  return inFlight
}

/**
 * Ends the session in this browser and asks Capsa to revoke both of its tokens.
 *
 * The browser's copy goes first and unconditionally: signing out is something the person did, and
 * a server that cannot be reached does not get to keep them signed in. A revocation that never
 * lands leaves tokens that lapse on their own, which is the lesser failure.
 *
 * The pair is read and cleared under the session lock, so a renewal already under way cannot write
 * a fresh pair back after it has gone.
 */
export const endSession = async () => {
  const session = await underLock(() => {
    const ending = {token: getItem('authToken'), refreshToken: getItem('refreshToken')}

    removeItem('authToken')
    removeItem('refreshToken')

    return ending
  })

  if (!session.token) return

  try {
    await post({query: print(SIGN_OUT), variables: {input: session}})
  } catch {
    // Already signed out here; see above.
  }
}
